import * as React from 'react'

import { ContextMenuSeparator, EditorContextMenuShell } from '../editor-context-menu-shell'
import { MenuActionRows, type MenuActions } from '../menu-action-rows'
import {
  CHECK_SPELLING_ROW,
  CONVERT_TEXT_TO_SHAPE_ROW,
  EDIT_TEXT_ROW,
  OBJECT_ACTION_ROWS,
  PATH_COMMAND_ROWS,
} from './constants/object-menu-rows'
import type { ObjectMenuActionId } from './types/object-menu-action-id'

export interface ObjectContextMenuProps {
  open: boolean
  position: { x: number; y: number }
  onOpenChange: (open: boolean) => void
  viewportBoundsRef?: React.RefObject<HTMLElement | null>
  /**
   * Single text box, shapes cleared — show text-only rows (spelling, path, etc.).
   * Must match the condition used for the `editText` action.
   */
  showTextObjectCommands?: boolean
  /**
   * Pen-tool / path shape selected — show "Edit Path" and "Text on Path" at the top.
   */
  showPathCommands?: boolean
  /**
   * Every row's behavior and state, keyed by row id (see `OBJECT_ACTION_ROWS` for the shared
   * rows). Some rows only render while their action has a `run`:
   * - `editText`: an "Edit Text" row is shown first (single text-frame selection).
   * - `convertTextToShape`: gated independently of `showTextObjectCommands` — the caller leaves
   *   `run` unset for a linked-chain frame so the row is hidden outright (WMPCM-6956).
   * - `checkSpelling`: also needs `showTextObjectCommands`.
   */
  actions: MenuActions<ObjectMenuActionId>
}

export function ObjectContextMenu({
  open,
  position,
  onOpenChange,
  viewportBoundsRef,
  showTextObjectCommands = false,
  showPathCommands = false,
  actions,
}: ObjectContextMenuProps) {
  const handleItemClick = (run?: () => void) => {
    run?.()
    onOpenChange(false)
  }

  return (
    <EditorContextMenuShell
      open={open}
      position={position}
      onOpenChange={onOpenChange}
      viewportBoundsRef={viewportBoundsRef}
    >
      {/*
        WMPCM-9514 AC-1: exactly ONE Text on Path row, for every combination of gates.

        `showPathCommands` is true for any shape selection and `editText` is set for a text
        frame, so a text box used to match both and render Edit Path and Text on Path twice —
        harmless while `textOnPath` was never supplied, immediately visible once it is. The
        `editText` block below owns the rows whenever it renders, because the AC (and
        `object-context-menu.test.tsx`) puts them *below* Edit Text for a text box.
      */}
      {showPathCommands && !actions.editText?.run && (
        <>
          <MenuActionRows
            rows={PATH_COMMAND_ROWS}
            actions={actions}
            onItemClick={handleItemClick}
          />
          <ContextMenuSeparator />
        </>
      )}

      {/*
        This section used to carry a third, lowercase "Text on path" row. Removed with WMPCM-9514
        — it was a third copy of a command that already renders above, and it never reached a
        user anyway (`center-canvas/components/canvas-context-menus.tsx` hardcodes
        `showTextObjectCommands={false}`).
      */}
      {showTextObjectCommands && actions.checkSpelling?.run ? (
        <>
          <MenuActionRows
            rows={CHECK_SPELLING_ROW}
            actions={actions}
            onItemClick={handleItemClick}
          />
          <ContextMenuSeparator />
        </>
      ) : null}

      {/*
        WMPCM-6956: own gate, deliberately NOT inside the `showTextObjectCommands` section above.
        That flag also controls Check Spelling, so switching the whole section on just to reach
        this one row would surface Check Spelling too. Visibility is the action's `run`: the
        caller leaves it unset for a linked-chain frame, so the row is hidden rather than
        disabled, per the AC.
      */}
      {actions.convertTextToShape?.run ? (
        <>
          <MenuActionRows
            rows={CONVERT_TEXT_TO_SHAPE_ROW}
            actions={actions}
            onItemClick={handleItemClick}
          />
          <ContextMenuSeparator />
        </>
      ) : null}

      {actions.editText?.run && (
        <>
          <MenuActionRows rows={EDIT_TEXT_ROW} actions={actions} onItemClick={handleItemClick} />
          <MenuActionRows
            rows={PATH_COMMAND_ROWS}
            actions={actions}
            onItemClick={handleItemClick}
          />
          <ContextMenuSeparator />
        </>
      )}

      <MenuActionRows rows={OBJECT_ACTION_ROWS} actions={actions} onItemClick={handleItemClick} />
    </EditorContextMenuShell>
  )
}

export default ObjectContextMenu
